import CheckIcon from "@mui/icons-material/Check";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";

import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import LoadingButton from "_shared/_views/loading/loading-button";
import EmailAddressInput from "_shared/email-addresses/_views/email-address-input";
import EmailAddress from "_shared/email-addresses/email-address";
import Guid from "_shared/guids/guid";
import NameInput from "_shared/names/_views/name-input";
import Name from "_shared/names/name";
import axios, { HttpStatusCode } from "axios";
import EntityAgreementAPIService, {
  InvalidEntityAgreementError,
} from "legal-entities/agreements/_api/entity-agreement-api-service";
import EntityAgreement from "legal-entities/agreements/entity-agreement";
import EntityNameInput from "legal-entities/entity-names/_views/inputs/entity-name-input";
import EntityName from "legal-entities/entity-names/entity-name";
import { useNavigate, useParams } from "react-router";
import { useSession } from "users/__session/session-context";

const PageContainer = styled(Box)(({ theme }) => ({
  height: "100%",
  overflow: "auto",
  padding: theme.spacing(4, 2),
  textAlign: "center",
}));
const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));
const CheckList = styled(List)(({ theme }) => ({
  margin: "auto",
  maxWidth: theme.spacing(80),
}));
const Instructions = styled(Typography)(({ theme }) => ({
  margin: theme.spacing(2, "auto", 1),
  maxWidth: theme.spacing(100),
}));
const AcceptButton = styled(LoadingButton)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));
const HelperText = styled(Container)(({ theme }) => ({
  color: theme.palette.text.secondary,
  margin: theme.spacing(2, "auto"),
  padding: theme.spacing(2),
}));

export default function EntityAgreementPage() {
  const [originalAgreement, setOriginalAgreement] = useState<EntityAgreement>();
  const [validEntityAgreement, setValidEntityAgreement] = useState<EntityAgreement>();
  const [formEntityName, setFormEntityName] = useState<EntityName>();
  const [formAdministratorName, setFormAdministratorName] = useState<Name>();
  const [formAdministratorEmail, setFormAdministratorEmail] = useState<EmailAddress>();
  const [formSkipAdminCreation, setFormSkipAdminCreation] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const session = useSession();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  useEffect(() => {
    async function loadAgreement(abortController: AbortController): Promise<EntityAgreement> {
      let agreement: EntityAgreement;
      const sessionAgreement = session.openEntityAgreements.find(
        (agreement: EntityAgreement) => agreement.id.value === params.id,
      );
      if (sessionAgreement) return sessionAgreement;
      if (!params.id) throw new Error("No agreement ID provided");
      const service = new EntityAgreementAPIService(session);
      agreement = await service.getEntityAgreementById(new Guid(params.id), abortController);
      return agreement;
    }
    const abortController = new AbortController();
    loadAgreement(abortController)
      .then((agreement: EntityAgreement) => initAgreement(agreement))
      .catch((error) => {
        console.error(error);
        enqueueSnackbar("Entity agreement is currently", { variant: "error" });
      });
    return () => abortController.abort();
  }, [params.id, session.openEntityAgreements.length]);

  function initAgreement(agreement?: EntityAgreement) {
    if (!agreement) throw new Error("Can't init empty agreement");
    setOriginalAgreement(agreement);
    setFormEntityName(agreement.entityName);
    setFormAdministratorName(agreement.administratorName);
    setFormAdministratorEmail(agreement.administratorEmail);
  }

  async function handleAcceptAgreement() {
    setSaving(true);

    try {
      if (!originalAgreement) throw new Error("Original agreement is missing");
      if (!validEntityAgreement) throw new InvalidEntityAgreementError("Agreement is invalid");
      const service = new EntityAgreementAPIService(session);
      const finalAgreement = await service.updateEntityAgreement(originalAgreement, validEntityAgreement);

      const authResponse = await service.acceptEntityAgreement(finalAgreement);

      session.initializeFromAuthResponse(authResponse);

      navigate("/dashboard", { replace: true });
    } catch (error: unknown) {
      if (axios.isCancel(error)) {
        navigate("/dashboard");
        return;
      }

      console.error(error);

      let message = "Couldn't accept agreement. Please try again";
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest) {
        message = String(error.response?.data);
      }

      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (
      !originalAgreement ||
      !formEntityName ||
      (!formSkipAdminCreation && (!formAdministratorName || !formAdministratorEmail))
    ) {
      setValidEntityAgreement(undefined);
      return;
    }
    try {
      setValidEntityAgreement(
        new EntityAgreement({
          id: originalAgreement.id,
          entityName: formEntityName,
          administratorName: formAdministratorName,
          administratorEmail: formAdministratorEmail,
        }),
      );
    } catch {
      setValidEntityAgreement(undefined);
    }
  }, [formEntityName, formAdministratorName, formAdministratorEmail, formSkipAdminCreation]);

  return (
    <PageContainer>
      <Grid container direction="column">
        <Grid component="form">
          <Title variant="h4">Terms Of Use</Title>
          <EntityNameInput
            label="Organization Name"
            entityName={formEntityName}
            variant="outlined"
            onChange={(updatedEntityName?: EntityName) => {
              setFormEntityName(updatedEntityName);
            }}
          />
        </Grid>
        <Grid>
          <CheckList disablePadding>
            <ListItem
              secondaryAction={
                <Tooltip
                  disableFocusListener
                  title={
                    "You can skip creating an additional Legal Entity Administrator fow now, " +
                    "and create one or more administrators at a later date"
                  }
                  placement="bottom"
                  arrow
                >
                  <IconButton size="small">
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemButton disableGutters onClick={() => setFormSkipAdminCreation(!formSkipAdminCreation)}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    color="primary"
                    checked={!formSkipAdminCreation}
                    onChange={(event) => setFormSkipAdminCreation(!event.target.checked)}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    formSkipAdminCreation
                      ? "Optional: Create Additional Entity Administrator?"
                      : "Creating Additional Entity Administrator"
                  }
                />
              </ListItemButton>
            </ListItem>
            {!formSkipAdminCreation && (
              <ListItem disableGutters>
                <Instructions>
                  Enter contact details for another person who is legally able to sign contracts on behalf of the
                  organization:
                </Instructions>
              </ListItem>
            )}
          </CheckList>
        </Grid>
        {!formSkipAdminCreation && (
          <Grid component="form">
            <NameInput
              idPrefix="entity-agreement-admin"
              variant="outlined"
              name={formAdministratorName}
              onChange={(updatedName?: Name) => {
                setFormAdministratorName(updatedName);
              }}
            />
            <EmailAddressInput
              idPrefix="entity-agreement-admin"
              variant="outlined"
              emailAddress={formAdministratorEmail}
              onChange={(updatedEmailAddress?: EmailAddress) => {
                setFormAdministratorEmail(updatedEmailAddress);
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === KeyboardKey.Enter && validEntityAgreement) {
                  handleAcceptAgreement();
                }
              }}
            />
          </Grid>
        )}
        <Grid component="form">
          <AcceptButton
            id="entity-agreement-submit-button"
            variant="contained"
            color="primary"
            loading={saving}
            disabled={saving || !validEntityAgreement}
            startIcon={<CheckIcon />}
            onClick={handleAcceptAgreement}
          >
            Accept Agreement
          </AcceptButton>
        </Grid>
        <Grid>
          <HelperText>
            <Typography>By clicking the accept button above you agree to be bound by the following terms</Typography>
          </HelperText>
        </Grid>
        <Grid>{/* <LegalEntityTerms termsId={this.state.termsId} /> */}</Grid>
      </Grid>
    </PageContainer>
  );
}
